import {
  ApolloLink,
  Observable,
  type ObservableSubscription,
} from '@apollo/client';

import { ensureGuestSession } from './ensure-guest-session';

/**
 * Holds every guest operation until `createPublicSession` has resolved, then
 * applies the session headers and forwards it.
 *
 * Why a link and not a provider that gates rendering: the first GraphQL call in
 * the guest app is `siteBranding`, fired by `WhiteLabelThemedContent` in
 * `_app`, which sits above the guest page tree — a gate inside the guest
 * providers would never cover it. A link is the one chokepoint every operation
 * passes through, including ones added later.
 *
 * Why the headers are applied here rather than in `makeClient`'s
 * `requestHeaders`: `makeClient` composes `from([authMiddleware, errorLink,
 * ...links, httpLink])`, so `authMiddleware` — which is what calls
 * `requestHeaders()` — runs before anything passed in `links`. Reading the
 * session there would read it before this link has awaited the mint, which is
 * the exact race this link exists to close.
 *
 * `createPublicSession` itself goes out through `packages/auth`'s raw `fetch`,
 * not Apollo, so gating Apollo can't deadlock the mint.
 */
export function guestAuthLink(url: string): ApolloLink {
  return new ApolloLink(
    (operation, forward) =>
      new Observable((observer) => {
        let subscription: ObservableSubscription | undefined;
        let cancelled = false;

        ensureGuestSession(url)
          // ensureGuestSession is written not to reject, but a throw here would
          // strand the operation forever — forward it unauthenticated instead.
          .catch((error) => {
            console.error('[guest-auth] ensureGuestSession threw', error);
            return undefined;
          })
          .then((session) => {
            if (cancelled) return;

            if (session) {
              operation.setContext(({ headers = {} }) => ({
                headers: { ...headers, ...session },
              }));
            }

            subscription = forward(operation).subscribe(observer);
          });

        return () => {
          cancelled = true;
          subscription?.unsubscribe();
        };
      })
  );
}
